import { parseThemeBlock, type ThemeBlock } from "../shared/presentation";
import { boundedKeys, exactKeys, GameSpecValidationError, integer, record, text } from "../shared/validators";

export interface PaddleDuelSpec {
  readonly version: 1;
  readonly kind: "paddle-duel";
  readonly title: string;
  readonly seed: number;
  readonly theme: ThemeBlock;
  readonly params: {
    readonly arenaWidth: number;
    readonly arenaHeight: number;
    readonly paddleHeight: number;
    readonly ballSpeed: number;
    readonly aiSpeed: number;
    readonly pointsToWin: number;
    /** 1 = versus the capped AI (default); 2 = local two-player, one paddle per side of the touchscreen. */
    readonly players?: number;
  };
}

/** A two-paddle rally: pong, tennis, air hockey -- any brief about volleying a ball past an opponent. */
export function parsePaddleDuelSpec(root: Record<string, unknown>): PaddleDuelSpec {
  exactKeys(root, ["version", "kind", "title", "seed", "theme", "params"], "game spec");
  if (root.kind !== "paddle-duel") throw new GameSpecValidationError("unsupported game kind");

  const params = record(root.params, "params");
  boundedKeys(params, ["arenaWidth", "arenaHeight", "paddleHeight", "ballSpeed", "aiSpeed", "pointsToWin"], ["players"], "params");
  const players = params.players === undefined ? undefined : integer(params.players, "params.players", 1, 2);

  return {
    version: 1,
    kind: "paddle-duel",
    title: text(root.title, "title", 1, 80),
    seed: integer(root.seed, "seed", 0, 4_294_967_295),
    theme: parseThemeBlock(root.theme),
    params: {
      arenaWidth: integer(params.arenaWidth, "params.arenaWidth", 480, 1_280),
      arenaHeight: integer(params.arenaHeight, "params.arenaHeight", 320, 800),
      paddleHeight: integer(params.paddleHeight, "params.paddleHeight", 60, 220),
      ballSpeed: integer(params.ballSpeed, "params.ballSpeed", 220, 600),
      aiSpeed: integer(params.aiSpeed, "params.aiSpeed", 90, 420),
      pointsToWin: integer(params.pointsToWin, "params.pointsToWin", 1, 9),
      ...(players === undefined ? {} : { players }),
    },
  };
}
